import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useInterviewSSE } from "@/hooks/useInterviewSSE";
import { MonthView } from "../shared/view/MonthView";
import { WeekView } from "../shared/view/WeekView";
import { DayView } from "../shared/view/DayView";
import { InterviewFormModal } from "../shared/dialog/InterviewFormModal";
import { hasPermission } from "@/types";
import type { Interview, AuthUser } from "@/types";

type CalView = "day" | "week" | "month";

interface Notification { id: number; candidateName: string; type: string; scheduledAt: string }

function getWeekStart(d: Date) {
  const s = new Date(d);
  s.setDate(s.getDate() - s.getDay());
  s.setHours(0, 0, 0, 0);
  return s;
}

function headerLabel(view: CalView, date: Date) {
  if (view === "month")
    return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  if (view === "day")
    return date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const ws = getWeekStart(date);
  const we = new Date(ws); we.setDate(we.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(ws)} – ${fmt(we)}, ${we.getFullYear()}`;
}

interface Props {
  authUser: AuthUser;
}

export function CalendarPage({ authUser }: Props) {
  const canWrite = hasPermission(authUser, "interviews:write");
  const [view, setView] = useState<CalView>("week");
  const [date, setDate] = useState(new Date());
  const [modal, setModal] = useState<{ open: boolean; interview?: Interview; defaultDate?: Date }>({ open: false });
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["interviews"],
    queryFn: () => api.listInterviews(),
    refetchInterval: 60_000,
  });
  const interviews = data?.interviews ?? [];

  const move = useMutation({
    mutationFn: ({ id, scheduled_at }: { id: number; scheduled_at: string }) =>
      api.updateInterview(id, { scheduled_at }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interviews"] }),
  });

  const handleDrop = useCallback((id: number, newDate: Date) => {
    if (!canWrite) return;
    move.mutate({ id, scheduled_at: newDate.toISOString() });
  }, [move, canWrite]);

  // SSE notifications: show a toast for each upcoming interview not yet notified
  const notifiedIds = new Set(notifications.map((n) => n.id));
  useInterviewSSE((upcoming) => {
    const fresh = upcoming.filter((iv) => !notifiedIds.has(iv.id));
    if (fresh.length > 0) {
      setNotifications((prev) => [
        ...prev,
        ...fresh.map((iv) => ({
          id: iv.id,
          candidateName: (iv as any).candidate_name ?? "Candidate",
          type: iv.interview_type,
          scheduledAt: iv.scheduled_at,
        })),
      ]);
    }
  });

  function navigate(dir: -1 | 1) {
    setDate((prev) => {
      const d = new Date(prev);
      if (view === "day") d.setDate(d.getDate() + dir);
      else if (view === "week") d.setDate(d.getDate() + 7 * dir);
      else d.setMonth(d.getMonth() + dir);
      return d;
    });
  }

  const sharedProps = {
    interviews,
    onInterviewClick: (iv: Interview) => setModal({ open: true, interview: iv }),
    onSlotClick: canWrite ? (d: Date) => setModal({ open: true, defaultDate: d }) : () => {},
    onInterviewDrop: handleDrop,
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border-card px-4 py-3">
        <button onClick={() => setDate(new Date())}
          className="rounded-btn border border-border-card px-3 py-1.5 text-xs font-medium text-near-black hover:bg-surface-secondary">
          Today
        </button>
        <div className="flex items-center gap-0.5">
          <button onClick={() => navigate(-1)} className="flex h-7 w-7 items-center justify-center rounded-btn hover:bg-surface-secondary">
            <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <span className="min-w-[180px] text-center text-sm font-semibold text-near-black">{headerLabel(view, date)}</span>
          <button onClick={() => navigate(1)} className="flex h-7 w-7 items-center justify-center rounded-btn hover:bg-surface-secondary">
            <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {(["day", "week", "month"] as CalView[]).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`rounded-btn px-3 py-1 text-xs font-medium capitalize transition-colors
                ${view === v ? "bg-near-black text-white" : "text-text-secondary hover:bg-surface-secondary hover:text-near-black"}`}>
              {v}
            </button>
          ))}
          {canWrite && (
            <button onClick={() => setModal({ open: true })}
              className="ml-2 rounded-btn bg-rausch px-3 py-1.5 text-xs font-medium text-white hover:bg-rausch/90">
              + Interview
            </button>
          )}
        </div>
      </div>

      {/* Calendar body */}
      {view === "month" && <MonthView currentDate={date} {...sharedProps} />}
      {view === "week" && <WeekView weekStart={getWeekStart(date)} {...sharedProps} />}
      {view === "day" && <DayView date={date} {...sharedProps} />}

      {/* Create / Edit modal */}
      {modal.open && (
        <InterviewFormModal
          defaultDate={modal.defaultDate}
          interview={modal.interview}
          onClose={() => setModal({ open: false })}
          onSaved={() => setModal({ open: false })}
        />
      )}

      {/* Upcoming interview notifications (bottom-right) */}
      {notifications.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {notifications.map((n) => (
            <div key={n.id} className="flex max-w-xs items-start gap-3 rounded-card bg-near-black p-4 shadow-xl">
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Interview in ~10 min</p>
                <p className="text-xs text-white/70">
                  {n.candidateName} · {n.type} at{" "}
                  {new Date(n.scheduledAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </p>
              </div>
              <button onClick={() => setNotifications((p) => p.filter((x) => x.id !== n.id))}
                className="text-white/60 hover:text-white">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
